import type { EntityState } from "@reduxjs/toolkit"
import { createEntityAdapter } from "@reduxjs/toolkit"
import type { Notification } from "./model"
import { Operation, baseApi, extractChannelEvent, subscribe } from "@/base"
import type { EntityChange } from "@/base"
import type { Uuid } from "@/utils"
import { invariant } from "@/utils"

export const adapter = createEntityAdapter<Notification>({
  sortComparer: (a, b) =>
    new Date(a.validAfter).getTime() - new Date(b.validAfter).getTime(),
})

const api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getNotifications: build.query<
      EntityState<Notification, Uuid>,
      void,
      Notification[]
    >({
      query: () => "notifications",
      transformResponse: (response) =>
        adapter.setAll(adapter.getInitialState(), response),
      onCacheEntryAdded: async (
        _arg,
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData, dispatch },
      ) => {
        const { meta } = await cacheDataLoaded
        invariant(meta?.response)
        const [channelName, eventName] = extractChannelEvent(meta.response)

        const unbind = await subscribe(
          channelName,
          () => dispatch(baseApi.endpoints.ablyAuth.initiate()).unwrap(),
          {
            [eventName]: (update: EntityChange<Notification>) => {
              updateCachedData((draft) => {
                switch (update.operation) {
                  case Operation.Insert:
                  case Operation.Update:
                    return adapter.upsertOne(draft, update.payload)
                  case Operation.Delete:
                    return adapter.removeOne(draft, update.payload)
                }
              })
            },
          },
        )

        await cacheEntryRemoved
        unbind()
      },
    }),
  }),
})

export const { useGetNotificationsQuery } = api
